import { selectClass } from "@/lib/formStyles";
import { UserRequest } from "api/types";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getUsersByRole } from "services/users";
import { roles } from "../../../constants/roles";
import ModalUI from "../../legacy/Modal";

/*
 * Confirmation de suppression d'un médecin — partagée par l'écran
 * « Personnel médecin » et par l'écran d'administration « Utilisateurs ».
 *
 * Côté serveur, supprimer un médecin est une désactivation assortie d'un
 * transfert : `DELETE /users/{id}` exige alors le paramètre
 * `replacementDoctorId`, et réaffecte à ce médecin les patients suivis et
 * les entrées de file d'attente du compte supprimé. Le choix du
 * remplaçant est donc obligatoire, d'où cette modale dédiée plutôt que la
 * confirmation générique.
 */

// La liste des médecins n'est pas paginée côté interface : on demande une
// page large, comme les listes déroulantes de `Patient/index.tsx`.
const DOCTORS_PAGE_SIZE = 200;

const formatDoctorName = (doctor?: UserRequest | null) =>
  [doctor?.firstName, doctor?.lastName].filter(Boolean).join(" ").trim();

export type DeleteDoctorDialogProps = {
  /** Médecin dont le compte va être supprimé. */
  doctor: UserRequest | null;
  open: boolean;
  /**
   * Appelé quand l'utilisateur valide. Le nom du remplaçant est fourni en
   * second argument pour que l'appelant puisse l'afficher dans son message
   * de succès sans recharger la liste des médecins de son côté.
   */
  onConfirm: (replacementDoctorId: number, replacementDoctorName: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

const DeleteDoctorDialog = ({
  doctor,
  open,
  onConfirm,
  onCancel,
  isLoading = false,
}: DeleteDoctorDialogProps) => {
  const [replacementDoctorId, setReplacementDoctorId] = useState("");

  // Réinitialise le choix à chaque ouverture pour ne pas conserver le
  // remplaçant sélectionné lors d'une suppression précédente.
  useEffect(() => {
    if (open) setReplacementDoctorId("");
  }, [open]);

  const { data: doctorsResult, isFetching: isLoadingDoctors } = useQuery(
    ["delete-doctor-replacements"],
    () => getUsersByRole(roles.ROLE_DOCTOR, "", 0, DOCTORS_PAGE_SIZE),
    { enabled: open, staleTime: 60000 }
  );

  const allDoctors: UserRequest[] = (doctorsResult as any)?.data?.content || [];
  // Le médecin supprimé ne peut pas être son propre remplaçant (400 côté
  // serveur) : on le retire de la liste plutôt que de laisser l'utilisateur
  // découvrir l'erreur après coup.
  const replacementDoctors = allDoctors.filter(
    (candidate: any) => String(candidate?.id) !== String((doctor as any)?.id)
  );

  const selectedDoctor = replacementDoctors.find(
    (candidate: any) => String(candidate?.id) === replacementDoctorId
  );
  // `doctorsResult` reste indéfini tant que la requête n'a pas répondu :
  // sans ce garde, l'avertissement « aucun médecin disponible » clignoterait
  // pendant le chargement.
  const hasLoadedDoctors = doctorsResult != null && !isLoadingDoctors;
  const hasNoReplacementDoctor =
    hasLoadedDoctors && replacementDoctors.length === 0;
  const canConfirm = !isLoading && selectedDoctor != null;

  const handleConfirm = () => {
    if (!selectedDoctor) return;
    onConfirm(Number((selectedDoctor as any).id), formatDoctorName(selectedDoctor));
  };

  return (
    <ModalUI
      isOpen={open}
      onClose={() => {
        if (!isLoading) onCancel();
      }}
      styles={{ width: "min(480px, 92vw)", padding: 0 }}
    >
      <div className="flex flex-col">
        <div className="flex items-start gap-4 px-6 pt-6">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600 ring-1 ring-rose-100">
            <i className="bx bx-transfer-alt text-2xl" />
          </div>
          <div className="space-y-1">
            <p className="text-[11px] uppercase tracking-[0.32em] text-rose-600 font-semibold">
              Action irréversible
            </p>
            <h2 className="text-lg font-semibold text-slate-900 leading-tight">
              Supprimer le médecin
            </h2>
            <p className="text-sm text-slate-500">
              Le compte de{" "}
              <span className="font-medium text-slate-800">
                Dr {formatDoctorName(doctor)}
              </span>{" "}
              sera supprimé. Ses patients suivis et ses entrées de la liste
              d'attente seront transférés au médecin que vous désignez
              ci-dessous.
            </p>
          </div>
        </div>

        <div className="px-6 pt-5">
          <label
            htmlFor="replacement-doctor"
            className="block text-sm font-medium text-slate-700"
          >
            Affecter les patients à
          </label>
          <div className="relative">
            <select
              id="replacement-doctor"
              className={selectClass}
              value={replacementDoctorId}
              onChange={(event) => setReplacementDoctorId(event.target.value)}
              disabled={isLoading || hasNoReplacementDoctor}
            >
              <option value="">— Choisir un médecin —</option>
              {replacementDoctors.map((candidate: any) => (
                <option key={candidate?.id} value={candidate?.id}>
                  {formatDoctorName(candidate)}
                </option>
              ))}
            </select>
            <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
          </div>
          {hasNoReplacementDoctor && (
            <p className="mt-2 text-sm text-rose-600">
              Aucun autre médecin actif n'est disponible pour recevoir les
              patients. Créez d'abord un médecin, puis recommencez.
            </p>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 inline-flex items-center justify-center h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-60"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-[1.6] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-sm font-medium shadow-lg shadow-rose-600/25 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <i className="bx bx-loader-alt animate-spin text-base" />
                <span>Transfert en cours…</span>
              </>
            ) : (
              <>
                <i className="bx bx-transfer-alt text-base" />
                <span>Supprimer et transférer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </ModalUI>
  );
};

export default DeleteDoctorDialog;
